import { doGET, doPATCH, doPOST } from '../../utils/HttpUtils';
import { AlyveIssueInput, AlyvePolicy, AlyvePolicyCreateInput, AlyvePolicyMember, AlyvePolicyStatus, AlyvePolicyType } from '../../types/alyve';
import { UserData } from '../../types/user';

const BASE_URL = '/policies';

function unwrap<T>(response: any): T {
  if (response?.error || response?.status >= 400) {
    throw new Error(response?.message || 'Alyve request failed');
  }
  return (response?.data?.data ?? response?.data) as T;
}

export async function fetchAlyvePolicies(status?: AlyvePolicyStatus | 'all', corporateId?: string) {
  const query = new URLSearchParams();
  if (status && status !== 'all') query.set('status', status);
  if (corporateId && corporateId !== 'general') query.set('corporateId', corporateId);
  const response = await doGET(`${BASE_URL}${query.toString() ? `?${query.toString()}` : ''}`);
  return unwrap<AlyvePolicy[]>(response) || [];
}

export async function fetchAlyveUsers(corporateId?: string) {
  const query = new URLSearchParams({
    userType: 'user',
    page: '1',
    limit: '100',
  });
  if (corporateId && corporateId !== 'general') query.set('corporateId', corporateId);
  const users: UserData[] = [];
  for (let page = 1; ; page++) {
    query.set('page', String(page));
    const response = await doGET(`/users?${query.toString()}`);
    const payload = unwrap<{ users?: UserData[] } | UserData[]>(response);
    const entries = Array.isArray(payload) ? payload : payload?.users || [];
    users.push(...entries);
    if (entries.length < 100) return users;
  }
}

export async function createAlyvePolicy(payload: AlyvePolicyCreateInput) {
  const response = await doPOST(BASE_URL, payload);
  return unwrap<AlyvePolicy>(response);
}

export async function createLoggedInAlyveSso(policyType?: AlyvePolicyType | '') {
  const response = await doPOST(`${BASE_URL}/sso`, policyType ? { policyType } : {});
  return unwrap<{ policy: AlyvePolicy; redirectUrl: string; created: boolean }>(response);
}

export async function createUserAlyveSso(userId: string, policyType?: AlyvePolicyType | '') {
  const response = await doPOST(`${BASE_URL}/users/${userId}/sso`, policyType ? { policyType } : {});
  return unwrap<{ policy: AlyvePolicy; redirectUrl: string; created: boolean }>(response);
}

export async function fetchAlyvePolicy(policyId: string) {
  const response = await doGET(`${BASE_URL}/${policyId}`);
  return unwrap<AlyvePolicy>(response);
}

export async function createAlyvePolicySso(policyId: string) {
  const response = await doPOST(`${BASE_URL}/${policyId}/sso`, {});
  return unwrap<{ redirectUrl: string }>(response);
}

export async function endorseAlyveMembers(
  policyId: string,
  payload: { members: AlyvePolicyMember[]; policyStartDate?: string; policyEndDate?: string }
) {
  const response = await doPATCH(`${BASE_URL}/${policyId}/members`, payload);
  return unwrap<AlyvePolicy>(response);
}

export async function issueAlyvePolicy(policyId: string, payload: AlyveIssueInput) {
  const response = await doPOST(`${BASE_URL}/${policyId}/issue`, payload);
  return unwrap<AlyvePolicy>(response);
}

export async function cancelAlyvePolicy(policyId: string) {
  const response = await doPOST(`${BASE_URL}/${policyId}/cancel`, {});
  return unwrap<{ refundAmount?: number }>(response);
}
